import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from './Footer';
import './FAQPageNew.css';

const LEGAL_DISCLAIMERS = [
  {
    title: 'Important Legal Notice',
    content: 'Mindmark is an AI-powered tool that assists with identifying potential compliance issues based on AHPRA advertising guidelines, the Psychology Board Code of Conduct, and mandatory notification requirements. This tool should not replace professional legal advice. Always consult with a legal professional or your professional indemnity insurer for compliance matters specific to your Australian psychology practice.'
  },
  {
    title: 'AI-Powered Analysis',
    content: 'This tool uses artificial intelligence to help identify potential violations against Section 133 of the National Law and other regulatory requirements. While we strive for accuracy based on official guidelines, AI can make mistakes. All results should be reviewed by a qualified professional before making compliance decisions for your practice.'
  },
  {
    title: 'No Liability',
    content: "Use of Mindmark is at your own risk. We are not responsible for any consequences arising from reliance on the tool's analysis, including but not limited to AHPRA complaints, fines, or regulatory action. This tool assists with compliance checking but does not guarantee compliance with AHPRA regulations."
  }
];

const FAQS = [
  {
    question: 'What does Mindmark do?',
    answer: 'Mindmark helps identify potential compliance issues in your Australian psychology practice content. You can check website pages, paste text, or upload documents (PDF, DOCX, TXT), and our AI analyzes them against AHPRA advertising guidelines, the Psychology Board Code of Conduct, and mandatory notification requirements. It flags issues like testimonials, outcome guarantees, improper use of specialist titles, gifts or discounts without terms and conditions, and urgency tactics.'
  },
  {
    question: 'How does it work?',
    answer: 'Paste text, enter a website URL, or upload a document and our AI analyzes the content against official regulations. For websites, you can check a single page or crawl up to 10 pages across your site. You receive a detailed report with specific text flagged, explanations of why it may violate guidelines, and suggested compliant alternatives. You can also use Compliance Chat to ask questions about regulations directly.'
  },
  {
    question: 'Is this legal advice?',
    answer: 'No. Mindmark is an educational tool that assists with identifying potential compliance issues. It should not replace professional legal advice. For compliance decisions specific to your Australian psychology practice, always consult a lawyer or your professional indemnity insurer.'
  },
  {
    question: 'Which regulations does it check?',
    answer: 'Mindmark checks against three key sources: the AHPRA Guidelines for Advertising a Regulated Health Service (December 2020), the Psychology Board Code of Conduct (December 2025), and the Mandatory Notifications Guidelines (March 2020). It covers areas including testimonials, unreasonable expectations, specialist titles, gifts and inducements, professional boundaries, and more.'
  },
  {
    question: 'How accurate is it?',
    answer: "The tool uses advanced AI trained on official AHPRA guidelines, the Code of Conduct, and mandatory notification requirements, but it's not perfect. Always review results with professional judgment. Some potential violations may be missed, and some flagged items may be acceptable in your specific context. This is a support tool, not a guarantee of compliance."
  },
  {
    question: 'What data do you collect?',
    answer: "Content submitted for compliance checks (text, URLs, documents) is processed in real-time and not stored on our servers — your check history is kept locally in your browser to help uphold client confidentiality. Documents stored in the Document Repository are kept securely in encrypted storage and are only accessible by you. Compliance Chat conversations are stored securely on our servers to maintain context across sessions, and you can delete any chat session at any time. Your account information is securely stored for authentication purposes only."
  },
  {
    question: 'How much does it cost?',
    answer: 'Mindmark offers 3 free compliance checks per month. For more, the Basic plan (A$39/month) includes 50 checks per month and Compliance Chat access. The Pro plan (A$79/month) includes unlimited checks, unlimited chat, and priority support. All plans include document storage — Free: 3 documents, Basic: 50, Pro: 200. Annual billing is also available. Cancel anytime.'
  },
  {
    question: 'What is Compliance Chat?',
    answer: 'Compliance Chat lets you ask questions about AHPRA regulations, the Code of Conduct, and mandatory notification requirements. It uses your actual regulation documents to provide answers with specific citations, so you can quickly get guidance on compliance scenarios. Available on Basic and Pro plans.'
  },
  {
    question: 'What file types can I upload to the Document Repository?',
    answer: 'You can upload PDF, DOC, DOCX, PNG, JPG, and TXT files. Each file can be up to 10MB in size.'
  },
  {
    question: 'How are my stored documents secured?',
    answer: 'Documents are stored in private, encrypted storage isolated to your account. Only you can access your files — they are not shared with other users, third parties, or used for AI training. If you delete your account, all stored documents are permanently removed.'
  },
  {
    question: 'What happens to my documents if I downgrade my plan?',
    answer: "Your existing documents remain fully accessible — you can view, download, and delete them. You just won't be able to upload new ones until your document count is within your new plan's limit."
  },
  {
    question: 'How many documents can I store?',
    answer: 'Free accounts can store up to 3 documents, Basic plans up to 50, and Pro plans up to 200. Each file can be up to 10MB in size.'
  },
  {
    question: "What if it doesn't work on my website?",
    answer: 'Our tool works on 95%+ of Australian psychology practice websites (Squarespace, Wix, WordPress, and custom sites). If URL scanning fails (due to bot protection or technical limitations), you can always paste your content manually or upload documents for analysis.'
  },
  {
    question: 'Is Mindmark affiliated with AHPRA?',
    answer: 'No. Mindmark is an independent tool created to help Australian psychologists maintain advertising compliance. We are not affiliated with, endorsed by, or connected to AHPRA. For official AHPRA guidance, visit ahpra.gov.au.'
  }
];

function FAQPageNew({ user, onLogout }) {
  const [openIndex, setOpenIndex] = useState(null);
  const navigate = useNavigate();

  const handleGetStarted = () => {
    if (user) {
      navigate('/');
    } else {
      navigate('/signup');
    }
  };

  return (
    <div className="faq-page-new">
      <Navbar user={user} onLogout={onLogout} />

      <div className="faq-container-new">
        {/* Hero */}
        <div className="faq-hero-new">
          <h1>FAQ & Legal Information</h1>
          <p>Important information about using Mindmark</p>
        </div>

        {/* Legal Disclaimers */}
        <section className="legal-section-new">
          <h2>Legal Disclaimers</h2>
          <p className="legal-intro">Please read these important notices before using Mindmark</p>

          <div className="disclaimers-grid-new">
            {LEGAL_DISCLAIMERS.map((disclaimer, index) => (
              <div key={index} className="disclaimer-card-new">
                <div className="disclaimer-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="24" height="24">
                    <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
                    <line x1="12" y1="9" x2="12" y2="13"/>
                    <line x1="12" y1="17" x2="12.01" y2="17"/>
                  </svg>
                </div>
                <h3>{disclaimer.title}</h3>
                <p>{disclaimer.content}</p>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ Section */}
        <section className="faq-section-new">
          <h2>Frequently Asked Questions</h2>

          <div className="faq-list-new">
            {FAQS.map((faq, index) => (
              <div
                key={index}
                className={`faq-item-new ${openIndex === index ? 'open' : ''}`}
              >
                <button
                  className="faq-question-new"
                  onClick={() => setOpenIndex(openIndex === index ? null : index)}
                >
                  <span>{faq.question}</span>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                    <polyline points={openIndex === index ? "18 15 12 9 6 15" : "6 9 12 15 18 9"}/>
                  </svg>
                </button>
                {openIndex === index && (
                  <p className="faq-answer-new">{faq.answer}</p>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="faq-cta-new">
          <h2>Ready to check your website?</h2>
          <p>Get started with 3 free compliance checks</p>
          <button className="btn-primary btn-large" onClick={handleGetStarted}>
            Get Started
          </button>
        </section>
      </div>

      <Footer />
    </div>
  );
}

export default FAQPageNew;
